// One continuous, RCM-anchored difficulty score (≈0.25–4, snapped to 0.25) for
// any catalog entry — drills and songs alike, so they're directly comparable.
// 0.25–1.0 = Preparatory (a two-hand blocked-chord folk tune lives here, NOT grade 2);
// ~1.0–1.5 = RCM grade 1 (hand independence begins); ~2 = grade 2; ~3–4 = grades 3–4.
// Texture dominates; key accidentals and hand-independence refine. Pure leaf.
// Weights are calibrated against the judge (songs/difficulty-eval.json).
import type { CatalogEntry } from "./catalog";
import type { Features } from "./difficulty-features";
import { KEY_ACCIDENTALS, TEXTURE_RANK, textureGrade } from "./difficulty-tables";
import { isScaleAtom, scaleOctaves } from "./scale-atoms";

const snap = (n: number): number => Math.round(n * 4) / 4;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** RCM-anchored difficulty from extracted features. Additive: a Preparatory base (0.25) plus
 *  small monotonic increments. Weights are NNLS-fit to the judge (songs/difficulty-eval.json);
 *  every term is non-negative so more of any feature never lowers the grade. The
 *  rhythm/reading/expression sub-projects add terms here.
 *
 *  Anchoring (the load-bearing decision): a two-hand blocked-chord folk tune is *Preparatory*,
 *  not RCM grade 2 — block texture adds nothing (textureGrade rank 1 = 0), so such a tune scores
 *  ~0.5–1.0 from its melodic features alone. 90 BPM is the neutral render tempo, so `tempo` only
 *  counts above ~95; `coordination` separates locked hands (Prep) from independent (grade 1+). */
export function grade(f: Features): number {
  const texture = textureGrade(f.textureRank);
  // 90 BPM is the flat render tempo of generated tunes → only genuinely faster pieces score here.
  const tempo = clamp01((f.tempo - 95) / 85) * 0.9;
  const density = clamp01((f.notesPerBar - 5) / 9) * 0.7;
  // 0.35 for ≤0.25 beats (32nd notes or shorter), 0.14 for ≤0.5 (eighth-note subdivisions)
  const subdivision =
    f.smallestSubdivision <= 0.25 ? 0.35 : f.smallestSubdivision <= 0.5 ? 0.14 : 0;
  const rhythm = clamp01((f.rhythmVariety - 2) / 4) * 0.1;
  const range = clamp01(f.rangeSemitones / 48) * 0.75;
  const shifts = clamp01(f.handShifts / 45) * 0.15;
  const coordination = f.coordination * 0.3;
  const key = f.accidentals * 0.125;
  const raw =
    0.25 + texture + tempo + density + subdivision + rhythm + range + shifts + coordination + key;
  return Math.max(0.25, snap(raw));
}

function gradeFromTags(entry: CatalogEntry): number {
  const uses = entry.uses ?? [];
  const teaches = entry.teaches ?? [];
  const atoms = [...uses, ...teaches];
  // Scale atoms gate (in `uses`) but score by octave when *taught* (in `teaches`) — so a
  // multi-octave scale drill ranks up, while a song's required-scale blocker never inflates it.
  const scaleRank = Math.max(0, ...teaches.filter(isScaleAtom).map(scaleOctaves));
  const hasTexture = atoms.some((a) => a in TEXTURE_RANK) || scaleRank > 0;
  if (!hasTexture) return entry.level ?? 1; // sourced piece w/o texture tags → its grade
  const texture = Math.max(scaleRank, 0, ...atoms.map((a) => TEXTURE_RANK[a] ?? 0));
  const handIndep = atoms.includes("hand-independence") ? 0.5 : 0;
  const acc = KEY_ACCIDENTALS[entry.key ?? ""] ?? 0;
  // Coarse fallback on the same Preparatory-anchored scale as grade(): block = base, the
  // texture ladder + accidentals + hand-independence refine. Used only when features are absent.
  return Math.max(0.25, snap(0.25 + textureGrade(texture) + acc * 0.125 + handIndep));
}

/** Difficulty of a catalog entry on the shared RCM-anchored scale. */
export function difficulty(entry: CatalogEntry): number {
  if (entry.features) return grade(entry.features);
  return gradeFromTags(entry);
}
